/** Pay cycle: 25th of previous month → 25th of end month. Key = YYYY-MM (end month). */

import {
    PUBLIC_HOLIDAYS,
    toDateKey,
} from "./calendarAttendance";

export const parsePeriodKey = (key) => {
    const [y, m] = key.split("-").map(Number);
    return { year: y, month: m - 1 };
};

export const periodKeyFromDate = (date, payCycle = { startDay: 25 }) => {
    const d = new Date(date);
    const start = parseInt(payCycle?.startDay || 25, 10);
    const periodEndMonth = new Date(d.getFullYear(), d.getMonth() + (d.getDate() >= start ? 1 : 0), 1);
    return `${periodEndMonth.getFullYear()}-${String(periodEndMonth.getMonth() + 1).padStart(2, "0")}`;
};

export const addMonthsToKey = (key, delta) => {
    const { year, month } = parsePeriodKey(key);
    const d = new Date(year, month + delta, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

export const buildPeriodFromKey = (key, payCycle = { startDay: 25 }) => {
    const { year, month } = parsePeriodKey(key);
    const startDay = parseInt(payCycle?.startDay || 25, 10);
    let periodStart, periodEnd, paymentDate;
    if (startDay === 1) {
        periodStart = new Date(year, month, 1);
        periodEnd = new Date(year, month + 1, 0);
        paymentDate = new Date(year, month + 1, 0);
    } else {
        periodStart = new Date(year, month - 1, startDay);
        periodEnd = new Date(year, month, startDay - 1);
        paymentDate = new Date(year, month, startDay);
    }
    const payMonth = periodEnd.toLocaleString("en-IN", { month: "long", year: "numeric" });
    const startMo = periodStart.toLocaleString("en-IN", { month: "short" });
    const startYr = periodStart.getFullYear();
    const endMo = periodEnd.toLocaleString("en-IN", { month: "short" });
    const endYr = periodEnd.getFullYear();
    const payPeriod = periodStart.getDate() + " " + startMo + " " + startYr + " - " + periodEnd.getDate() + " " + endMo + " " + endYr;
    const payDate = paymentDate.getDate() + " " + paymentDate.toLocaleString("en-IN", { month: "long" }) + " " + paymentDate.getFullYear();
    const workDays = Math.round((periodEnd - periodStart) / (1000 * 60 * 60 * 24)) + 1;

    return { key, payMonth, payPeriod, payDate, periodStart, periodEnd, workDays };
};

export const getCurrentPayPeriod = (payCycle) => buildPeriodFromKey(periodKeyFromDate(new Date(), payCycle), payCycle);

export const PAY_PERIOD_HISTORY_MONTHS = 36;

/** All downloadable pay months (newest first). Not limited by joining date. */
export const getRecentPayPeriodOptions = (count = PAY_PERIOD_HISTORY_MONTHS, payCycle) => {
    const current = getCurrentPayPeriod(payCycle);
    const options = [];

    for (let i = 0; i < count; i++) {
        const key = addMonthsToKey(current.key, -i);
        const p = buildPeriodFromKey(key, payCycle);
        options.push({ value: key, label: p.payMonth });
    }

    return options;
};

export const getPayPeriodMonthBounds = (count = PAY_PERIOD_HISTORY_MONTHS, payCycle) => {
    const current = getCurrentPayPeriod(payCycle);
    return {
        min: addMonthsToKey(current.key, -(count - 1)),
        max: current.key,
    };
};

export const getLastNPeriodsFromKey = (endKey, n, payCycle) => {
    const keys = [];
    for (let i = n - 1; i >= 0; i--) {
        keys.push(addMonthsToKey(endKey, -i));
    }
    return keys.map(k => buildPeriodFromKey(k, payCycle));
};

export const listPeriodsBetweenKeys = (fromKey, toKey, payCycle) => {
    const start = fromKey <= toKey ? fromKey : toKey;
    const end = fromKey <= toKey ? toKey : fromKey;
    const keys = [];
    let k = start;
    while (k <= end) {
        keys.push(k);
        if (k === end) break;
        k = addMonthsToKey(k, 1);
    }
    return keys.map(k => buildPeriodFromKey(k, payCycle));
};

export const filterExpensesInPeriod = (expenses, periodStart, periodEnd) => {
    const start = new Date(periodStart).setHours(0, 0, 0, 0);
    const end = new Date(periodEnd).setHours(23, 59, 59, 999);

    return (expenses || []).filter((e) => {
        const raw = e.date || e.processedAt || e.createdAt;
        if (!raw) return false;
        const t = new Date(raw).getTime();
        return t >= start && t <= end;
    });
};

const getDateKey = (value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return toDateKey(date.getFullYear(), date.getMonth(), date.getDate());
};

const getPeriodDateKeys = (period) => {
    const start = startOfDay(period?.periodStart || new Date());
    const endInclusive = startOfDay(period?.periodEnd || new Date());
    const keys = [];

    for (let date = new Date(start); date <= endInclusive; date.setDate(date.getDate() + 1)) {
        keys.push(toDateKey(date.getFullYear(), date.getMonth(), date.getDate()));
    }

    return keys;
};

export const getAttendanceQueryBounds = (periods = []) => {
    const starts = periods
        .map((period) => startOfDay(period?.periodStart))
        .filter((date) => !Number.isNaN(date.getTime()));
    const ends = periods
        .map((period) => startOfDay(period?.periodEnd))
        .filter((date) => !Number.isNaN(date.getTime()));

    if (!starts.length || !ends.length) return null;

    const start = new Date(Math.min(...starts));
    const endInclusive = new Date(Math.max(...ends));

    return {
        startDate: getDateKey(start),
        endDate: getDateKey(endInclusive),
    };
};

export const computePeriodAttendanceStats = (
    employee = {},
    period = {},
    { attendance = [], leaves = [], today = new Date() } = {},
) => {
    const attendanceByDate = new Map((attendance || []).map((record) => [record?.date, record]));
    const attendanceStartKey = getDateKey(employee?.attendanceStartDate || employee?.joiningDate);
    const todayKey = getDateKey(today);
    const dayMeta = {};
    let approvedLeaveDays = 0;
    let approvedLopDays = 0;
    let presentDays = 0;
    let absentDays = 0;

    getPeriodDateKeys(period).forEach((dateKey) => {
        const [year, monthOne, day] = dateKey.split("-").map(Number);
        const month = monthOne - 1;
        const dateObj = new Date(year, month, day);
        const attendanceRecord = attendanceByDate.get(dateKey);
        // Leave approval alone does not alter attendance or payroll.
        const leave = null;
        const status = String(attendanceRecord?.status || "").toLowerCase();
        const hasCheckIn = status !== "absent" && Boolean(attendanceRecord?.checkIn);
        const isBeforeAttendanceStart = attendanceStartKey && dateKey < attendanceStartKey;
        const isSunday = dateObj.getDay() === 0;
        const holiday = PUBLIC_HOLIDAYS.find((item) => item.month === month && item.day === day);
        const isPast = dateKey < todayKey;

        const meta = {
            dateKey,
            leave,
            isNotIn: false,
        };

        if (isBeforeAttendanceStart) {
            // Attendance had not started for this employee yet.
        } else if (status === "compensated") {
            approvedLeaveDays += 1;
        } else if (leave) {
            approvedLeaveDays += 1;
            if (String(leave.leaveType || "").toLowerCase() === "lop") {
                approvedLopDays += 1;
            }
        } else if (status === "absent") {
            absentDays += 1;
            meta.isNotIn = true;
        } else if (hasCheckIn) {
            presentDays += 1;
        } else if (!holiday && !isSunday && isPast) {
            absentDays += 1;
            meta.isNotIn = true;
        }

        dayMeta[dateKey] = meta;
    });

    return {
        presentDays,
        absentDays,
        leaveTakenDays: approvedLeaveDays,
        lopDays: approvedLopDays,
        approvedLeaveDays,
        autoCompensatedLeaveDays: 0,
        autoLopDays: 0,
    };
};

export const computeSlipAmounts = (employee, period, periodExpenses, isCurrentPeriod, attendanceStats = null) => {
    const config = period?.payrollConfig || {};
    const workDays = period.workDays;
    const syncedLopDays = attendanceStats && Number.isFinite(Number(attendanceStats.lopDays))
        ? Number(attendanceStats.lopDays)
        : null;
    const manualLopDays = isCurrentPeriod ? Number(employee?.lossOfPay || 0) : 0;
    const lopDays = syncedLopDays === null ? manualLopDays : Math.max(syncedLopDays, manualLopDays);
    const leaveTakenDays = attendanceStats && Number.isFinite(Number(attendanceStats.leaveTakenDays))
        ? Number(attendanceStats.leaveTakenDays)
        : lopDays;
    const paidDays = Math.max(0, workDays - lopDays);
    const grossBase = Number(period?.grossOverride ?? employee?.salary ?? 0);
    
    let stayAllowance = 0;
    let stayAllowanceBase = 0;
    let carryForwardApplied = 0;
    if (config.useStayAllowance !== undefined) {
        stayAllowance = config.useStayAllowance ? (Number(config.stayAllowanceAmount) || 0) : 0;
        stayAllowanceBase = stayAllowance;
    } else {
        stayAllowanceBase = employee?.hasStayAllowance ? 4000 : 0;
        carryForwardApplied = employee?.stayAllowanceCarryForward ? Math.min(stayAllowanceBase, Number(period?.stayAllowanceCarryForwardPreviousBalance || 0)) : 0;
        stayAllowance = Number(period?.stayAllowanceDeduction ?? Math.max(0, stayAllowanceBase - carryForwardApplied));
    }
    const legacyBasic = grossBase - stayAllowance;

    // Dynamic Computations
    const basicAmount = config.useBasic === true 
        ? Math.round((grossBase * (Number(config.basicPercent) || 0)) / 100) 
        : (config.useBasic === false ? 0 : legacyBasic);
    const hraAmount = config.useHRA ? Math.round((basicAmount * (Number(config.hraPercent) || 0)) / 100) : 0;
    const daAmount = config.useDA ? Math.round((basicAmount * (Number(config.daPercent) || 0)) / 100) : 0;
    const otherAllowancesAmount = Math.max(0, grossBase - basicAmount - hraAmount - daAmount);

    const otAmount = config.useOvertime ? Math.round((basicAmount / 26 / 8) * (Number(config.otHours) || 0) * (Number(config.otRate) || 0)) : 0;
    const bonusAmount = config.useBonus ? Math.round((grossBase * (Number(config.bonusPercent) || 0)) / 100) : 0;
    
    const gross = grossBase + otAmount + bonusAmount;

    const pfAmount = config.usePF ? Math.round((basicAmount * (Number(config.pfPercent) || 0)) / 100) : 0;
    const pfEmployerAmount = config.usePF ? Math.round((basicAmount * (Number(config.pfEmployerPercent) || 0)) / 100) : 0;
    const esiAmount = config.useESI ? Math.round((gross * (Number(config.esiPercent) || 0)) / 100) : 0;
    const esiEmployerAmount = config.useESI ? Math.round((gross * (Number(config.esiEmployerPercent) || 0)) / 100) : 0;
    const profTaxAmount = config.useProfTax ? Number(config.profTaxAmount) || 0 : 0;

    const lopDed = basicAmount > 0 && workDays > 0 ? Math.round((basicAmount / workDays) * lopDays) : 0;
    const totalExpenses = (periodExpenses || []).reduce((sum, e) => sum + (e.amount || 0), 0);
    const totalDed = stayAllowance + pfAmount + esiAmount + profTaxAmount + lopDed;
    const net = Math.max(0, gross - totalDed + totalExpenses);

    const perDaySalary = Math.round(grossBase / 26);
    const perHourSalary = Math.round(perDaySalary / 8);
    const salaryForDaysWorked = Math.round(perDaySalary * paidDays);
    const gratuity = config.useGratuity ? Math.round((basicAmount + daAmount) * (15 / 26) * (Number(config.gratuityYears) || 0)) : 0;
    const ctcAmount = gross + pfEmployerAmount + esiEmployerAmount + (config.useGratuity ? gratuity : 0);
    const annualSalary = gross * 12;

    return {
        lopDays,
        leaveTakenDays,
        paidDays,
        gross,
        grossBase,
        hra: stayAllowance, // Keep for backward compatibility of Stay Allowance
        basic: basicAmount,
        lopDed,
        totalExpenses,
        totalDed,
        stayAllowance,
        stayAllowanceBase,
        stayAllowanceCarryForwardEnabled: employee?.stayAllowanceCarryForward === true,
        stayAllowanceCarryForwardApplied: carryForwardApplied,
        stayAllowanceCarryForwardEarned: Number(period?.stayAllowanceCarryForwardEarned || 0),
        stayAllowanceCarryForwardPreviousBalance: Number(period?.stayAllowanceCarryForwardPreviousBalance || 0),
        stayAllowanceCarryForwardNextBalance: Number(period?.stayAllowanceCarryForwardNextBalance || 0),
        stayAllowanceLeaveDays: Number(period?.stayAllowanceLeaveDays || 0),
        net,
        payrollConfig: config,
        basicAmount,
        hraAmount,
        daAmount,
        otherAllowancesAmount,
        otAmount,
        bonusAmount,
        pfAmount,
        pfEmployerAmount,
        esiAmount,
        esiEmployerAmount,
        profTaxAmount,
        perDaySalary,
        perHourSalary,
        salaryForDaysWorked,
        gratuity,
        ctcAmount,
        annualSalary
    };
};

export const getEffectiveSalaryForPeriod = (employee, salaryHistory = [], period) => {
    const periodEnd = new Date(period?.periodEnd || new Date()).getTime();
    const effective = [...(salaryHistory || [])]
        .filter((item) => {
            const time = new Date(item.effectiveDate).getTime();
            return Number.isFinite(time) && time <= periodEnd;
        })
        .sort((a, b) => new Date(b.effectiveDate) - new Date(a.effectiveDate))[0];

    return Number(effective?.amount ?? employee?.salary ?? 0);
};

const DAY_MS = 24 * 60 * 60 * 1000;

const startOfDay = (value) => {
    const date = new Date(value);
    date.setHours(0, 0, 0, 0);
    return date;
};

const endOfDay = (value) => {
    const date = new Date(value);
    date.setHours(23, 59, 59, 999);
    return date;
};

const countInclusiveDays = (start, end) => {
    const from = startOfDay(start);
    const to = startOfDay(end);
    if (to < from) return 0;
    return Math.floor((to - from) / DAY_MS) + 1;
};

export const getProratedSalaryForPeriod = (employee, salaryHistory = [], period) => {
    const periodStart = startOfDay(period?.periodStart || new Date());
    const periodEnd = endOfDay(period?.periodEnd || new Date());
    const totalDays = countInclusiveDays(periodStart, periodEnd);
    const joiningDate = employee?.joiningDate ? startOfDay(employee.joiningDate) : null;
    const joinsDuringPeriod = joiningDate && joiningDate > periodStart && joiningDate <= periodEnd;
    const sortedHistory = [...(salaryHistory || [])]
        .filter((item) => item?.effectiveDate)
        .sort((a, b) => new Date(a.effectiveDate) - new Date(b.effectiveDate));
    const baseline = sortedHistory.filter((item) => new Date(item.effectiveDate) <= periodStart).at(-1);
    const changesInPeriod = sortedHistory.filter((item) => {
        const effective = startOfDay(item.effectiveDate);
        return effective > periodStart && effective <= periodEnd;
    });
    const timeline = [
        { amount: joinsDuringPeriod ? 0 : Number(baseline?.amount ?? employee?.salary ?? 0), effectiveDate: periodStart },
        ...(joinsDuringPeriod && !changesInPeriod.some((item) => startOfDay(item.effectiveDate).getTime() === joiningDate.getTime())
            ? [{ amount: Number(baseline?.amount ?? employee?.salary ?? 0), effectiveDate: joiningDate }]
            : []),
        ...changesInPeriod.map((item) => ({
            amount: Number(item.amount || 0),
            effectiveDate: startOfDay(item.effectiveDate),
        })),
    ];

    const segments = timeline.map((item, index) => {
        const segmentStart = index === 0 ? periodStart : startOfDay(item.effectiveDate);
        const nextStart = timeline[index + 1]?.effectiveDate ? startOfDay(timeline[index + 1].effectiveDate) : null;
        const segmentEnd = nextStart
            ? endOfDay(new Date(nextStart.getFullYear(), nextStart.getMonth(), nextStart.getDate() - 1))
            : periodEnd;
        const days = countInclusiveDays(segmentStart, segmentEnd);
        const salary = Number(item.amount || 0);
        const proratedAmount = totalDays > 0 ? (salary / totalDays) * days : 0;
        return { from: segmentStart, to: segmentEnd, days, salary, proratedAmount };
    }).filter((segment) => segment.days > 0);

    return {
        gross: Math.round(segments.reduce((sum, segment) => sum + segment.proratedAmount, 0)),
        effectiveSalary: segments.at(-1)?.salary ?? Number(employee?.salary || 0),
        segments,
        totalDays,
    };
};

export const formatPeriodRangeLabel = (periods) => {
    if (!periods?.length) return "";
    if (periods.length === 1) return periods[0].payMonth;
    return `${periods[0].payMonth} – ${periods[periods.length - 1].payMonth}`;
};

/** Default custom span ending at anchorKey (YYYY-MM). */
export const defaultCustomRangeKeys = (anchorKey, spanMonths = 3, bounds, payCycle) => {
    const b = bounds || getPayPeriodMonthBounds(PAY_PERIOD_HISTORY_MONTHS, payCycle);
    let toKey = anchorKey || b.max;
    if (toKey > b.max) toKey = b.max;
    if (toKey < b.min) toKey = b.min;
    let fromKey = addMonthsToKey(toKey, -(spanMonths - 1));
    if (fromKey < b.min) fromKey = b.min;
    return { fromKey, toKey };
};

export const clampCustomRangeKeys = (fromKey, toKey, bounds, payCycle) => {
    const b = bounds || getPayPeriodMonthBounds(PAY_PERIOD_HISTORY_MONTHS, payCycle);
    const clamp = (k) => {
        if (!k) return b.min;
        if (k < b.min) return b.min;
        if (k > b.max) return b.max;
        return k;
    };
    let from = clamp(fromKey);
    let to = clamp(toKey);
    if (from > to) {
        [from, to] = [to, from];
    }
    return { fromKey: from, toKey: to };
};

export const numberToWords = (num) => {
    if (!num || isNaN(num) || num <= 0) return "Rupees Zero Only";
    const a = [
        "",
        "One",
        "Two",
        "Three",
        "Four",
        "Five",
        "Six",
        "Seven",
        "Eight",
        "Nine",
        "Ten",
        "Eleven",
        "Twelve",
        "Thirteen",
        "Fourteen",
        "Fifteen",
        "Sixteen",
        "Seventeen",
        "Eighteen",
        "Nineteen",
    ];
    const b = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
    const g = ["", "Thousand", "Lakh", "Crore"];
    const grp = (n) => {
        let s = "";
        if (n >= 100) {
            s += a[Math.floor(n / 100)] + " Hundred ";
            n %= 100;
        }
        if (n >= 20) {
            s += b[Math.floor(n / 10)] + " ";
            n %= 10;
        }
        if (n > 0) s += a[n] + " ";
        return s;
    };
    let n = Math.floor(num);
    const parts = [];
    parts.push(n % 1000);
    n = Math.floor(n / 1000);
    if (n > 0) {
        parts.push(n % 100);
        n = Math.floor(n / 100);
    }
    if (n > 0) {
        parts.push(n % 100);
        n = Math.floor(n / 100);
    }
    if (n > 0) parts.push(n);
    let w = "";
    for (let i = parts.length - 1; i >= 0; i--) {
        if (parts[i] > 0) w += grp(parts[i]) + (g[i] ? g[i] + " " : "");
    }
    return "Rupees " + w.trim() + " Only";
};

export const fmtInr = (v) => Number(v).toLocaleString("en-IN") + ".00";
